import dayjs, { type Dayjs, type OpUnitType, type QUnitType } from 'dayjs';
import { type DragState, type GanttData, type GanttHeaderMajorGroups, type GanttHeaderMinorGroups, ViewMode } from './types';

export const dayjsTz = (date?: Date | string | number | Dayjs | null) => dayjs(date).utc();

export const emptyRowsArray: { name: string; id: number }[] = Array(9).fill({
  name: '',
  id: Math.random().toString(),
});

export const calcVisibleWindow = (data: GanttData[], view: ViewMode, mode: 'interactive' | 'print') => {
  if (!data.length)
    return {
      windowStart: dayjsTz().startOf('year').toDate(),
      windowEnd: dayjsTz().endOf('year').toDate(),
      dataStart: dayjsTz().toDate(),
      dataEnd: dayjsTz().toDate(),
    };

  const [dataStart, dataEnd] = getAllItemsRange(data);

  if (!dataStart || !dataEnd) {
    return {
      windowStart: dayjsTz().startOf('year').toDate(),
      windowEnd: dayjsTz().endOf('year').toDate(),
      dataStart: dayjsTz().toDate(),
      dataEnd: dayjsTz().toDate(),
    };
  }

  const minimalRange = getMinimalRange(dataStart, dataEnd, view);
  const extendedRange = applyExtraGap(minimalRange.startDate, minimalRange.endDate, view, mode);

  return {
    windowStart: extendedRange.startDate,
    windowEnd: extendedRange.endDate,
    dataStart: dataStart,
    dataEnd: dataEnd,
  };
};

const applyExtraGap = (startDate: Dayjs, endDate: Dayjs, view: ViewMode, mode: 'interactive' | 'print') => {
  if (mode === 'print') return { startDate: startDate.toDate(), endDate: endDate.toDate() };

  switch (view) {
    case ViewMode.Weekly:
      return {
        startDate: startDate.startOf('month').subtract(3, 'month').toDate(),
        endDate: startDate.endOf('month').add(3, 'month').toDate(),
      };
    case ViewMode.Monthly: {
      return {
        startDate: startDate.startOf('month').subtract(12, 'month').toDate(),
        endDate: startDate.endOf('month').add(12, 'month').toDate(),
      };
    }
    case ViewMode.Quarterly:
      return {
        startDate: startDate.startOf('quarter').subtract(2, 'year').toDate(),
        endDate: startDate.endOf('quarter').add(3, 'year').toDate(),
      };
    case ViewMode.Yearly:
      return {
        startDate: startDate.startOf('year').subtract(2, 'year').toDate(),
        endDate: startDate.endOf('year').add(4, 'year').toDate(),
      };
    default:
      return {
        startDate: startDate.subtract(3, 'month').toDate(),
        endDate: startDate.add(3, 'month').toDate(),
      };
  }
};

export const getMinorsCount = (startDate: Date, endDate: Date, view: ViewMode) => {
  switch (view) {
    case ViewMode.Weekly:
      return dayjsTz(endDate).diff(startDate, 'd');
    case ViewMode.Monthly:
      return dayjsTz(endDate).diff(startDate, 'd');
    case ViewMode.Quarterly:
      return dayjsTz(endDate).diff(startDate, 'd');
    case ViewMode.Yearly:
      return dayjsTz(endDate).diff(startDate, 'd');
  }
};

export const getCellWidth = (view: ViewMode) => {
  switch (view) {
    case ViewMode.Weekly:
      return 50;
    case ViewMode.Monthly:
      return 10;
    case ViewMode.Quarterly:
      return 3.3;
    case ViewMode.Yearly:
      return 3;
    default:
      return 50;
  }
};

export const getViewConfig = (startDate: Date, endDate: Date, view: ViewMode) => {
  return {
    minorsCount: getMinorsCount(startDate, endDate, view) || 0,
    cellWidth: getCellWidth(view),
  };
};

export const getMajorLabel = (date: Dayjs, view: ViewMode) => {
  const currentDate = dayjsTz(date);
  const isCurrentYear = date.toDate().getFullYear() === new Date().getFullYear();

  const year = ` '${currentDate.format('YY')}`;
  const yearSuffix = !isCurrentYear ? year : '';

  switch (view) {
    case ViewMode.Quarterly:
      return `${date.startOf('quarter').format('MMM')} - ${date.endOf('quarter').format('MMM')}${yearSuffix}`;
    case ViewMode.Weekly:
    case ViewMode.Monthly:
      return date.format('MMM') + yearSuffix;
    case ViewMode.Yearly:
      return date.format('YYYY');
    default:
      return date.format('MMM') + yearSuffix;
  }
};

export const getMinorLabel = (date: Dayjs, view: ViewMode) => {
  switch (view) {
    case ViewMode.Weekly:
      return dayjsTz(date).format('D');
    case ViewMode.Weekly:
    case ViewMode.Monthly:
    case ViewMode.Yearly:
      return date.format('MMM');
    default:
      return dayjsTz(date).format('D');
  }
};

const isWithinRange = (today: Dayjs, target: Dayjs, scale: QUnitType | OpUnitType) => {
  return target.startOf(scale) < today && today < target.endOf(scale);
};

export const checkIfTodayGroup = (date: Dayjs, view: ViewMode) => {
  const today = dayjsTz(new Date());
  switch (view) {
    case ViewMode.Weekly:
      return isWithinRange(today, date, 'week');
    case ViewMode.Monthly:
      return isWithinRange(today, date, 'month');
    case ViewMode.Quarterly:
      return isWithinRange(today, date, 'quarter');
    case ViewMode.Yearly:
      return isWithinRange(today, date, 'year');
  }
};
export const majorGroupBy = (date: Dayjs, view: ViewMode) => {
  switch (view) {
    case ViewMode.Weekly:
      return date.format('w_YY'); //
    case ViewMode.Monthly:
      return date.format('M_YYYY');
    case ViewMode.Quarterly:
      return date.format('Q_YYYY');
    case ViewMode.Yearly:
      return date.format('YYYY');
    default:
      return date.format('M_YYYY');
  }
};

export const minorGroupBy = (date: Dayjs, view: ViewMode) => {
  switch (view) {
    case ViewMode.Yearly:
      return date.format('M_YYYY');
    default:
      return date.format('DD_MM_YYYY');
  }
};

export const getMinimalRange = (startDate: Date, endDate: Date, view: ViewMode) => {
  switch (view) {
    case ViewMode.Weekly:
      return {
        startDate: dayjsTz(startDate).subtract(1, 'day'),
        endDate: dayjsTz(endDate).add(1, 'day'),
      };
    case ViewMode.Monthly:
      return {
        startDate: dayjsTz(startDate).startOf('month'),
        endDate: dayjsTz(endDate).endOf('month'),
      };

    case ViewMode.Quarterly:
      return {
        startDate: dayjsTz(startDate).startOf('quarter'),
        endDate: dayjsTz(endDate).endOf('quarter'),
      };
    case ViewMode.Yearly:
      return {
        startDate: dayjsTz(startDate).startOf('year'),
        endDate: dayjsTz(endDate).endOf('year'),
      };
    default:
      return {
        startDate: dayjsTz(startDate).subtract(1, 'day'),
        endDate: dayjsTz(endDate).add(1, 'day'),
      };
  }
};

export const getAllItemsRange = (data: GanttData[]) => {
  const sortedByStartDate = [...data].sort((a, b) => dayjsTz(a.startDate).valueOf() - dayjsTz(b.startDate).valueOf());
  const sortedByDueDate = [...data].sort((a, b) => dayjsTz(b.dueDate).valueOf() - dayjsTz(a.dueDate).valueOf());

  return [
    sortedByStartDate[0].startDate ? dayjsTz(sortedByStartDate[0].startDate).startOf('day').toDate() : null,
    sortedByDueDate[0].dueDate ? dayjsTz(sortedByDueDate[0].dueDate).endOf('day').toDate() : null,
  ];
};

export const getMajorGroups = (minorsCount: number, windowStart: Date, view: ViewMode) => {
  const groups: GanttHeaderMajorGroups = {};

  for (let i = 0; i < minorsCount + 1; i++) {
    const currentDate = dayjsTz(windowStart).add(i, 'day');
    const mode = majorGroupBy(currentDate, view);
    const dayNumber = +currentDate.format('D');

    if (mode in groups) {
      groups[mode].items.push(dayNumber);
    } else {
      groups[mode] = {
        label: getMajorLabel(currentDate, view),
        items: [dayNumber],
        isTodayGroup: checkIfTodayGroup(currentDate, view),
      };
    }
  }

  return groups;
};

export const getMinorsGroup = (minorsCount: number, windowStart: Date, view: ViewMode) => {
  const groups: GanttHeaderMinorGroups = {};

  for (let i = 0; i < minorsCount + 1; i++) {
    const currentDate = dayjsTz(windowStart).add(i, 'day');
    const mode = minorGroupBy(currentDate, view);
    const dayNumber = +currentDate.format('D');

    if (mode in groups) {
      groups[mode].items.push(dayNumber);
    } else {
      groups[mode] = {
        label: getMinorLabel(currentDate, view),
        items: [dayNumber],
      };
    }
  }

  return groups;
};

export const getLeftOffset = (barStart: number, windowStart: number, cellWidth: number) => {
  const diffInSec = (barStart - windowStart) / 1000;
  const diffInDays = diffInSec / (3600 * 24);

  return diffInDays * cellWidth;
};

export const getWidth = (barStart: number, barEnd: number, cellWidth: number) => {
  const diffInSec = (barEnd - barStart) / 1000;
  const diffInDays = diffInSec / (3600 * 24);

  return diffInDays * cellWidth;
};

export const getDefaultDragState = (): DragState => ({
  startX: 0,
  targetEl: null,
  targetId: null,
  dragType: 'drag',
  initialLeft: 0,
  initialWidth: 0,
  newStart: null,
  newEnd: null,
});
